import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { TabTypeValue } from 'components/patient/concurrentReview/ConcurrentReviewSlideout';
import ConcurrentReviewHistoryList from 'components/patient/concurrentReview/HistoryList';
import RoutedConcurrentReviewSlideout from 'components/patient/concurrentReview/RoutedConcurrentReviewSlideout';
import CircularProgress from 'components/shared/CircularProgress';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { Paginated } from 'models/Paginated';
import Authorization from 'models/reviews/Authorization';
import { authorizationQueryKeys, indexReviews } from 'services/api/reviews';
import { queryClient } from 'services/http';
import { colors } from 'styles/theme/colors';
import { H2, Label } from 'styles/typography';

export default function ConcurrentReviewsPage() {
  const { locationEpisode } = useSelectedLocationEpisode();
  const navigate = useNavigate();

  const queryParams = useMemo(
    () => ({
      sortBy: 'createdAt desc',
      filters: {
        type: 'authorization',
        locationEpisodeId: locationEpisode?.id,
        'approvedThroughDate.not': 'null',
      },
      include: [
        'attachmentCount',
        'previousAuthorizationReview',
        'summaryJobForCurrentAttachments',
        'comparisonJobForCurrentSummary',
      ].join(','),
    }),
    [locationEpisode?.id]
  );

  const queryKey = authorizationQueryKeys.index({ ...queryParams });

  const { data: reviews, isLoading } = useQuery({
    queryKey,
    queryFn: ({ signal }) => indexReviews(queryParams as any, signal),
    select: (data) => data.data as Authorization[],
    enabled: !!locationEpisode?.id,
  });

  const handleViewAuthorization = (authorization: Authorization, initialTab: TabTypeValue = 'files') => {
    navigate(`${authorization.id}/${initialTab}`);
  };

  const handleCloseSlideout = (authorization?: Authorization) => {
    if (authorization) {
      // update the Concurrent Reviews list page with any changes made to the authorization in the slideout
      queryClient.setQueryData<Paginated<Authorization>>(queryKey, (results: Paginated<Authorization>) => {
        return {
          ...results,
          data: results.data.map((cachedAuthorization) =>
            cachedAuthorization.id === authorization.id ? authorization : cachedAuthorization
          ),
        };
      });
    }
  };

  return (
    <>
      <ConcurrentReviewsHeader>
        <ConcurrentReviewsPageTitle>Concurrent Reviews</ConcurrentReviewsPageTitle>
        <Label>View a history of clinical documentation and summaries for concurrent review</Label>
      </ConcurrentReviewsHeader>
      {isLoading ? (
        <LoadingContainer>
          <CircularProgress color={colors.primaryBlue} />
        </LoadingContainer>
      ) : (
        <ConcurrentReviewHistoryList history={reviews || []} onViewAuthorization={handleViewAuthorization} />
      )}
      <RoutedConcurrentReviewSlideout onClose={handleCloseSlideout} />
    </>
  );
}

const ConcurrentReviewsPageTitle = styled(H2)`
  font-size: 20px;
`;

const ConcurrentReviewsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;
